import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeFormParams, resetForm } from 'store/dealManagement/reducer';
import { getModalVisibilityConditions } from 'store/dealManagement/selectors';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import AdvertiserInfoConfirmationModal from 'components/common/Modals/AdvertiserInfoConfirmationModal';
import useUpdateDirectSalesCampaign from 'components/pages/Planner/hooks/DirectSalesPlannerActions/useUpdateDirectSalesCampaign';
import useProgrammaticPlannerActions from 'components/pages/Planner/hooks/useProgrammaticPlannerActions';
import { useHandleCampaignFieldChanges } from 'components/pages/Planner/hooks/DirectSalesPlannerActions/useHandleCampaignFieldChanges';

type SaveButtonProps = {
  handleSave: () => void;
};

interface Props {
  children?: (saveButtonProps: SaveButtonProps) => React.ReactElement;
}

export const SaveCampaign: React.FC<Props> = ({ children: saveButton }) => {
  const dispatch = useDispatch();
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const { saveDeal: saveDirectSalesDeal } = useUpdateDirectSalesCampaign();
  const { saveDeal: saveProgrammaticDeal } = useProgrammaticPlannerActions();
  const { shouldShowAdvertiserInfoModal } = useSelector(getModalVisibilityConditions);
  const { captureChangesBeforeSave, handleAfterSave } = useHandleCampaignFieldChanges();

  const [showAdvertiserInfoConfirmationModal, setShowAdvertiserInfoConfirmationModal] = useState(false);

  const handleSave = async (): Promise<void> => {
    if (isDirectSalesCampaignType) {
      captureChangesBeforeSave();

      const dealSavedSuccessfully = await saveDirectSalesDeal();

      if (dealSavedSuccessfully) {
        dispatch(changeFormParams({ isCampaignDrawerOpen: false }));
        await handleAfterSave();
      }

      return;
    }

    if (shouldShowAdvertiserInfoModal) {
      setShowAdvertiserInfoConfirmationModal(true);
      return;
    }

    saveProgrammaticDeal(null);
  };

  const handleUndoChanges = (): void => {
    dispatch(resetForm());
    setShowAdvertiserInfoConfirmationModal(false);
  };

  const handleConfirmChanges = (): void => {
    setShowAdvertiserInfoConfirmationModal(false);
    saveProgrammaticDeal(null);
  };

  return (
    <>
      {saveButton?.({ handleSave })}

      <AdvertiserInfoConfirmationModal
        isVisible={showAdvertiserInfoConfirmationModal}
        onUndoChanges={handleUndoChanges}
        onConfirm={handleConfirmChanges}
      />
    </>
  );
};
